/**
 * BulkUpdatePage - Bulk update users from CSV file
 *
 * Route: /admin/users/bulk-update
 *
 * Flow:
 * 1. User drops/selects CSV file
 * 2. Parse CSV and lookup existing users
 * 3. Show preview tables with diffs
 * 4. Submit changes sequentially
 */

import { useState, useCallback, useRef, useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { useAuth } from '@/common/hooks/useAuth'
import { useToast } from '@/common/hooks/useToast'
import { getErrorMessage } from '@/common/utils/errors'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { Icon } from '@/common/components/icon'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Table } from '@/common/components/table'
import { Tooltip } from '@/common/components/tooltip'
import { parseCSV } from '@/features/admin-users/utils/csvParse'
import { computeDiff, type DiffResult, type FieldDiff, type NewUserRow, type UpdatedUserRow } from '@/features/admin-users/utils/csvDiff'
import { downloadCSVTemplate } from '@/features/admin-users/utils/csvExport'
import { lookupUsersByEmail, createUser, updateUser } from '@/features/admin-users/api/adminUsers'
import * as styles from './bulk-update.css'

export const Route = createFileRoute({
  component: BulkUpdatePage,
})

// =============================================================================
// Types
// =============================================================================

type PageState =
  | { status: 'idle' }
  | { status: 'parsing' }
  | { status: 'looking-up' }
  | { status: 'preview'; file: File; diff: DiffResult }
  | { status: 'submitting'; file: File; diff: DiffResult; currentIndex: number }
  | { status: 'complete'; file: File; diff: DiffResult; successCount: number; errorCount: number }

// =============================================================================
// Component
// =============================================================================

function BulkUpdatePage() {
  const { roles, token } = useAuth()
  const { showSuccessToast, showErrorToast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [state, setState] = useState<PageState>({ status: 'idle' })
  const [isDragging, setIsDragging] = useState(false)

  // For tracking mutable row states during submission
  const [newUserRows, setNewUserRows] = useState<NewUserRow[]>([])
  const [updatedUserRows, setUpdatedUserRows] = useState<UpdatedUserRow[]>([])

  const isAdmin = roles.includes('admin')

  // Handle file selection
  const handleFile = useCallback(
    async (file: File) => {
      if (!token) return

      setState({ status: 'parsing' })

      try {
        // Parse CSV
        const parseResult = await parseCSV(file)

        if (parseResult.rows.length === 0 && parseResult.errors.length === 0) {
          showErrorToast('CSV file is empty')
          setState({ status: 'idle' })
          return
        }

        setState({ status: 'looking-up' })

        // Get unique emails from parsed rows
        const emails = parseResult.rows.map((r) => r.email)

        // Lookup existing users
        const lookupResult = await lookupUsersByEmail(token, emails)

        // Compute diff
        const diff = computeDiff(parseResult.rows, lookupResult.users, parseResult.errors)

        // Initialize row states
        setNewUserRows(diff.newUsers)
        setUpdatedUserRows(diff.updatedUsers)

        setState({ status: 'preview', file, diff })
      } catch (err) {
        showErrorToast(getErrorMessage(err) || 'Failed to process CSV')
        setState({ status: 'idle' })
      }
    },
    [token, showErrorToast]
  )

  // Drag handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      const files = e.dataTransfer.files
      if (files.length > 0) {
        const file = files[0]
        if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
          handleFile(file)
        } else {
          showErrorToast('Please select a CSV file')
        }
      }
    },
    [handleFile, showErrorToast]
  )

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) {
        handleFile(files[0])
      }
    },
    [handleFile]
  )

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // Reset to idle
  const handleReset = useCallback(() => {
    setState({ status: 'idle' })
    setNewUserRows([])
    setUpdatedUserRows([])
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  // Submit changes
  const handleSubmit = useCallback(async () => {
    if (state.status !== 'preview' || !token) return

    const { file, diff } = state
    let successCount = 0
    let errorCount = 0
    let currentIndex = 0

    setState({ status: 'submitting', file, diff, currentIndex: 0 })

    // Process new users first
    for (let i = 0; i < newUserRows.length; i++) {
      const row = newUserRows[i]
      currentIndex = i
      setState((s) =>
        s.status === 'submitting' ? { ...s, currentIndex } : s
      )

      // Mark as processing
      setNewUserRows((rows) =>
        rows.map((r, idx) => (idx === i ? { ...r, status: 'processing' } : r))
      )

      try {
        await createUser(token, {
          email: row.data.email,
          first_name: row.data.first_name,
          last_name: row.data.last_name,
          role: row.data.role,
          company_name: row.data.company_name,
          public_phone: row.data.public_phone,
          public_email: row.data.public_email,
          markets: row.data.markets,
          send_signup_email: row.data.send_signup_email,
        })

        setNewUserRows((rows) =>
          rows.map((r, idx) => (idx === i ? { ...r, status: 'success' } : r))
        )
        successCount++
      } catch (err) {
        setNewUserRows((rows) =>
          rows.map((r, idx) =>
            idx === i ? { ...r, status: 'error', error: getErrorMessage(err) || 'Failed to create user' } : r
          )
        )
        errorCount++
      }
    }

    // Process updated users
    for (let i = 0; i < updatedUserRows.length; i++) {
      const row = updatedUserRows[i]
      currentIndex = newUserRows.length + i
      setState((s) =>
        s.status === 'submitting' ? { ...s, currentIndex } : s
      )

      // Mark as processing
      setUpdatedUserRows((rows) =>
        rows.map((r, idx) => (idx === i ? { ...r, status: 'processing' } : r))
      )

      try {
        await updateUser(token, row.userId, {
          first_name: row.data.first_name,
          last_name: row.data.last_name,
          role: row.data.role,
          company_name: row.data.company_name,
          public_phone: row.data.public_phone,
          public_email: row.data.public_email,
          markets: row.data.markets,
        })

        setUpdatedUserRows((rows) =>
          rows.map((r, idx) => (idx === i ? { ...r, status: 'success' } : r))
        )
        successCount++
      } catch (err) {
        setUpdatedUserRows((rows) =>
          rows.map((r, idx) =>
            idx === i ? { ...r, status: 'error', error: getErrorMessage(err) || 'Failed to update user' } : r
          )
        )
        errorCount++
      }
    }

    setState({ status: 'complete', file, diff, successCount, errorCount })

    if (errorCount === 0) {
      showSuccessToast(`Successfully processed ${successCount} users`)
    } else {
      showErrorToast(`Completed with ${errorCount} errors`)
    }
  }, [state, token, newUserRows, updatedUserRows, showSuccessToast, showErrorToast])

  // Computed values for preview
  const hasValidationErrors = useMemo(() => {
    if (state.status !== 'preview') return false
    return state.diff.hasValidationErrors
  }, [state])

  const canSubmit = useMemo(() => {
    if (state.status !== 'preview') return false
    const hasChanges = newUserRows.length > 0 || updatedUserRows.length > 0
    return hasChanges && !hasValidationErrors
  }, [state, newUserRows, updatedUserRows, hasValidationErrors])

  // Non-admin unauthorized view
  if (!isAdmin) {
    return (
      <Stack gap="24" style={{ padding: '24px', height: '100%' }}>
        <Stack gap="4">
          <Text as="h1" variant="h4" weight="bold">
            Bulk Update Users
          </Text>
          <Text variant="bodySm" color="muted">
            Import users from CSV
          </Text>
        </Stack>
        <Stack gap="16" align="center" justify="center" style={{ flex: 1 }}>
          <Icon name="lock" size={48} style={{ color: '#999' }} />
          <Text as="h2" variant="bodyLg" weight="bold">
            Access Restricted
          </Text>
          <Text variant="body" color="muted" style={{ textAlign: 'center', maxWidth: 400 }}>
            Bulk user management is only available to administrators.
          </Text>
          <Link to="/properties">
            <Button>Go to Properties</Button>
          </Link>
        </Stack>
      </Stack>
    )
  }

  return (
    <div className={styles.container}>
      {/* Back link */}
      <Link to="/admin/users" className={styles.backLink}>
        <Icon name="arrow_back" size={16} />
        Back to Users
      </Link>

      {/* Header */}
      <div className={styles.header}>
        <Stack gap="4">
          <Text as="h1" variant="h4" weight="bold">
            Bulk Update Users
          </Text>
          <Text variant="bodySm" color="muted">
            Import users from CSV file
          </Text>
        </Stack>
      </div>

      {/* Idle state - show dropzone */}
      {state.status === 'idle' && (
        <>
          <div className={styles.description}>
            <Text variant="body">
              Upload a CSV file to create or update users in bulk. Download the sample template to
              see the expected format.
            </Text>
          </div>

          <div className={styles.templateLink}>
            <Button variant="secondary" onClick={downloadCSVTemplate}>
              <Icon name="download" size={18} />
              Download Sample CSV
            </Button>
          </div>

          <div
            className={isDragging ? styles.dropzone.dragging : styles.dropzone.default}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={openFilePicker}
          >
            <div className={styles.dropzoneIcon}>
              <Icon name="upload" size={48} />
            </div>
            <Text variant="bodyLg" weight="medium" className={styles.dropzoneText}>
              Drop CSV file here or click to browse
            </Text>
            <Text variant="bodySm" className={styles.dropzoneHint}>
              Accepts .csv files
            </Text>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={handleFileInput}
          />
        </>
      )}

      {/* Parsing state */}
      {state.status === 'parsing' && (
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="lg" />
          <Text variant="bodyLg">Parsing CSV file...</Text>
        </div>
      )}

      {/* Looking up state */}
      {state.status === 'looking-up' && (
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="lg" />
          <Text variant="bodyLg">Looking up existing users...</Text>
        </div>
      )}

      {/* Preview/Submitting/Complete state */}
      {(state.status === 'preview' || state.status === 'submitting' || state.status === 'complete') && (
        <>
          {/* File info */}
          <div className={styles.fileInfo}>
            <Icon name="description" size={24} />
            <Text className={styles.fileName}>{state.file.name}</Text>
            {state.status === 'preview' && (
              <Button variant="ghost" size="sm" onClick={handleReset} className={styles.removeFile}>
                <Icon name="close" size={18} />
              </Button>
            )}
          </div>

          {/* Parse errors */}
          {state.diff.parseErrors.length > 0 && (
            <div className={styles.parseErrors}>
              <Stack direction="row" gap="8" align="center">
                <Icon name="error" size={20} style={{ color: 'var(--color-error)' }} />
                <Text weight="medium">Parse Errors ({state.diff.parseErrors.length})</Text>
              </Stack>
              <ul className={styles.parseErrorsList}>
                {state.diff.parseErrors.map((err, i) => (
                  <li key={i}>
                    Row {err.row}: {err.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* New Users Table */}
          {newUserRows.length > 0 && (
            <div className={styles.previewSection}>
              <div className={styles.sectionHeader}>
                <Icon name="person_add" size={24} />
                <Text as="h2" variant="bodyLg" weight="bold">
                  New Users
                </Text>
                <Text variant="bodyLg" className={styles.sectionCount}>
                  ({newUserRows.length})
                </Text>
              </div>
              <div className={styles.tableContainer}>
                <Table.Root size="sm" pinnedFirst pinnedLast>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Role</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Company</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Welcome Email</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell align="center" width={60}>Status</Table.ColumnHeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {newUserRows.map((row) => (
                      <Table.Row key={row.data.email} className={styles.row[row.status]}>
                        <Table.Cell>{row.data.email}</Table.Cell>
                        <Table.Cell>
                          {row.data.first_name} {row.data.last_name}
                        </Table.Cell>
                        <Table.Cell>{row.data.role}</Table.Cell>
                        <Table.Cell>{row.data.company_name || '-'}</Table.Cell>
                        <Table.Cell>{row.data.send_signup_email ? 'Yes' : 'No'}</Table.Cell>
                        <Table.Cell align="center">
                          <StatusIcon status={row.status} error={row.error} />
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </div>
            </div>
          )}

          {/* Updated Users Table */}
          {updatedUserRows.length > 0 && (
            <div className={styles.previewSection}>
              <div className={styles.sectionHeader}>
                <Icon name="edit" size={24} />
                <Text as="h2" variant="bodyLg" weight="bold">
                  Updated Users
                </Text>
                <Text variant="bodyLg" className={styles.sectionCount}>
                  ({updatedUserRows.length})
                </Text>
              </div>
              <div className={styles.tableContainer}>
                <Table.Root size="sm" pinnedFirst pinnedLast>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Role</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Company</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Phone</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Public Email</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Markets</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell align="center" width={60}>Status</Table.ColumnHeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {updatedUserRows.map((row) => {
                      const getDiff = (field: string) => row.diffs.find((d) => d.field === field)
                      const firstNameDiff = getDiff('first_name')
                      const lastNameDiff = getDiff('last_name')
                      const roleDiff = getDiff('role')
                      const companyDiff = getDiff('company_name')
                      const phoneDiff = getDiff('public_phone')
                      const emailDiff = getDiff('public_email')
                      const marketsDiff = getDiff('markets')

                      return (
                        <Table.Row key={row.email} className={styles.row[row.status]}>
                          <Table.Cell>{row.email}</Table.Cell>
                          <Table.Cell>
                            {firstNameDiff || lastNameDiff ? (
                              <>
                                <span className={styles.diffOld}>
                                  {row.existingData.first_name} {row.existingData.last_name}
                                </span>
                                <span className={styles.diffArrow}> → </span>
                                <span className={styles.diffNew}>
                                  {row.data.first_name} {row.data.last_name}
                                </span>
                              </>
                            ) : (
                              <span className={styles.diffUnchanged}>
                                {row.existingData.first_name} {row.existingData.last_name}
                              </span>
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            <DiffCell diff={roleDiff} current={row.existingData.roles[0]} />
                          </Table.Cell>
                          <Table.Cell>
                            <DiffCell diff={companyDiff} current={row.existingData.company_name} />
                          </Table.Cell>
                          <Table.Cell>
                            <DiffCell diff={phoneDiff} current={row.existingData.public_phone} />
                          </Table.Cell>
                          <Table.Cell>
                            <DiffCell diff={emailDiff} current={row.existingData.public_email} />
                          </Table.Cell>
                          <Table.Cell>
                            <DiffCell diff={marketsDiff} current={row.existingData.markets?.join(', ')} />
                          </Table.Cell>
                          <Table.Cell align="center">
                            <StatusIcon status={row.status} error={row.error} />
                          </Table.Cell>
                        </Table.Row>
                      )
                    })}
                  </Table.Body>
                </Table.Root>
              </div>
            </div>
          )}

          {/* Unchanged Users Table */}
          {state.diff.unchangedUsers.length > 0 && (
            <div className={styles.previewSection}>
              <div className={styles.sectionHeader}>
                <Icon name="check_circle" size={24} style={{ color: 'var(--color-muted)' }} />
                <Text as="h2" variant="bodyLg" weight="bold">
                  Unchanged Users
                </Text>
                <Text variant="bodyLg" className={styles.sectionCount}>
                  ({state.diff.unchangedUsers.length})
                </Text>
              </div>
              <div className={styles.tableContainer}>
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {state.diff.unchangedUsers.map((user) => (
                      <Table.Row key={user.email}>
                        <Table.Cell>{user.email}</Table.Cell>
                        <Table.Cell>{user.firstName} {user.lastName}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className={styles.footer}>
            <div className={styles.footerInner}>
              <div className={styles.footerLeft}>
                <Link to="/admin/users">
                  <Button variant="outline" disabled={state.status === 'submitting'}>
                    Cancel
                  </Button>
                </Link>
                {hasValidationErrors && (
                  <div className={styles.errorSummary}>
                    <Icon name="error" size={18} />
                    <Text variant="bodySm">Fix validation errors before submitting</Text>
                  </div>
                )}
              </div>
              <div className={styles.actions}>
                {state.status === 'complete' ? (
                  <>
                    <Button variant="secondary" onClick={handleReset}>
                      Upload Another File
                    </Button>
                    <Link to="/admin/users">
                      <Button>Back to Users</Button>
                    </Link>
                  </>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={!canSubmit || state.status === 'submitting'}
                  >
                    {state.status === 'submitting' ? (
                      <>
                        <LoadingSpinner size="sm" />
                        Processing...
                      </>
                    ) : (
                      'Apply Bulk Updates'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// =============================================================================
// Helper Components
// =============================================================================

interface StatusIconProps {
  status: 'ready' | 'processing' | 'success' | 'error'
  error?: string
}

function StatusIcon({ status, error }: StatusIconProps) {
  switch (status) {
    case 'ready':
      return (
        <Tooltip content="Ready" delayDuration={0}>
          <span style={{ display: 'inline-flex' }}>
            <Icon name="arrow_upload_ready" size={18} className={styles.statusIcon.ready} />
          </span>
        </Tooltip>
      )
    case 'processing':
      return <LoadingSpinner size="sm" />
    case 'success':
      return (
        <Tooltip content="Success" delayDuration={0}>
          <span style={{ display: 'inline-flex' }}>
            <Icon name="check_circle" size={18} className={styles.statusIcon.success} />
          </span>
        </Tooltip>
      )
    case 'error':
      return (
        <Tooltip content={error || 'Error'} delayDuration={0}>
          <span style={{ display: 'inline-flex' }}>
            <Icon name="error" size={18} className={styles.statusIcon.error} />
          </span>
        </Tooltip>
      )
  }
}

interface DiffCellProps {
  diff: FieldDiff | undefined
  current: string | null | undefined
}

function DiffCell({ diff, current }: DiffCellProps) {
  if (diff) {
    return (
      <>
        <span className={styles.diffOld}>{diff.oldValue}</span>
        <span className={styles.diffArrow}> → </span>
        <span className={styles.diffNew}>{diff.newValue}</span>
      </>
    )
  }
  return <span className={styles.diffUnchanged}>{current || '-'}</span>
}
